// Switches the active account and reloads the app around the new session.

import { useState } from 'react';
import { useSetAtom } from 'jotai';
import { toast } from '@/components/ui/sonner';
import { useUserAccounts } from '@/hooks/use-user-accounts';
import { clearAccountScopedStorage } from '@/lib/account-storage';
import { clearQueryCache } from '@/lib/query-client';
import { authService } from '@/services';
import { selectedBrandAtom } from '@/stores/sentinel-workspace.store';
import { selectedBusinessUnitIdAtom } from '@/stores/business-unit.store';
import { resetSimulatorAtom } from '@/stores/websocket.store';

/**
 * Swaps the session to another account. Every caller must go through this: the tokens,
 * the cached queries and the per-account atoms have to move together, and the reload is
 * what re-initialises the WebSocket for the new account.
 */
export function useSwitchAccount() {
  const [isSwitching, setIsSwitching] = useState(false);
  const { accounts, currentAccountId } = useUserAccounts();
  const resetSimulator = useSetAtom(resetSimulatorAtom);
  const setSelectedBrand = useSetAtom(selectedBrandAtom);
  const setBusinessUnitId = useSetAtom(selectedBusinessUnitIdAtom);

  /**
   * `buId` is the brand to land on, for the switcher's nested brand list: it belongs to
   * the account being moved to, so it can only be applied once this has finished clearing
   * the previous account's. Left out, the new account opens on its own default.
   */
  const switchAccount = async (accountId: number, destination = '/', buId?: string): Promise<boolean> => {
    if (isSwitching) return false;
    const account = accounts.find((candidate) => candidate.id === accountId);
    // Already there — just record the brand so Sentinel skips its picker.
    if (accountId === currentAccountId) {
      if (account) setSelectedBrand({ id: account.id, name: account.name });
      return true;
    }

    setIsSwitching(true);
    try {
      const result = await authService.switchAccount(accountId);
      localStorage.setItem('accessToken', result.accessToken);
      localStorage.setItem('refreshToken', result.refreshToken);
      localStorage.setItem('currentUser', JSON.stringify(result.user));
      clearQueryCache();
      // Switching account swaps it under the same tab, exactly as impersonation does, so
      // the previous account's working data must not carry over. The selected BUSINESS
      // UNIT is the load-bearing one: a brand belongs to one account, and the reload below
      // rehydrates it straight out of localStorage — so without this every scoped screen
      // comes back filtered by an id the new account does not own, which reads as empty
      // lists rather than an error. The scenarios and the simulate-tools switch ride along
      // for the reasons in account-storage.ts.
      clearAccountScopedStorage();
      // AFTER the clear, never before: the selected brand is one of the keys it drops, so
      // a brand set by the caller first would be thrown away by the switch it asked for.
      if (buId) setBusinessUnitId(buId);
      // A brand is an account, so carry the choice over the reload.
      if (account) setSelectedBrand({ id: account.id, name: account.name });
      // The simulator scenario belongs to the account it was authored on.
      resetSimulator();
      toast.success('Account switched successfully');
      window.location.href = destination;
      return true;
    } catch {
      toast.error('Failed to switch account. Please try again.');
      return false;
    } finally {
      setIsSwitching(false);
    }
  };

  return { switchAccount, isSwitching };
}
